import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Euro, MapPin, Building2, Trash2, Gift, CalendarClock } from "lucide-react";
import { startCheckout } from "@/lib/payments";
import { isFreePeriod } from "@/lib/pricing";
import { Empty } from "@/components/bedrijf/Empty";
import { BEDRIJF_STATUS_LABEL } from "@/lib/offerteOptions";

function OfferteExpiry({ o, onExtend }) {
  if (!o.vervalt_op || o.status !== "open") return null;
  const verval = new Date(o.vervalt_op);
  const days = Math.ceil((verval.getTime() - Date.now()) / 86400000);
  const soon = days <= 5;
  const maxed = (o.verleng_count || 0) >= 1;
  const dateStr = verval.toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div data-testid="offerte-expiry-row" className={`mb-3 rounded-sm px-3 py-2 text-xs flex flex-wrap items-center gap-2 ${soon ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-slate-50 border border-slate-200 text-slate-600"}`}>
      <CalendarClock className="w-3.5 h-3.5 shrink-0" />
      <span className="font-semibold">Verloopt op {dateStr}{days >= 0 ? ` (${days} dag${days === 1 ? "" : "en"})` : " (verlopen)"}</span>
      {!maxed ? (
        <Button data-testid="extend-offerte-btn" onClick={() => onExtend?.(o.id)} size="sm"
          className="ml-auto h-7 bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold text-xs px-3">
          Verleng +1 maand
        </Button>
      ) : (
        <span className="ml-auto text-slate-400">Max. verlengd</span>
      )}
    </div>
  );
}

function ElementSummary({ elementen }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-3">
      {(elementen || []).map((e, i) => (
        <span key={`${e.type || "el"}-${i}`} className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">
          {e.aantal}× {e.type} {e.breedte && e.hoogte ? `(${e.breedte}×${e.hoogte}cm)` : ""}
        </span>
      ))}
    </div>
  );
}

export function MijnOffertes({ offertes, onDelete, onSendFree, onExtend }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {offertes.length === 0 && <Empty text="Je hebt nog geen offertes aangevraagd." />}
      {offertes.map((o) => (
        <div key={o.id} data-testid="mijn-offerte-card" className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-display text-lg font-bold text-slate-950">Offerteaanvraag</h3>
            <StatusBadge status={o.status === "open" ? "open" : "concept"} />
          </div>
          <ElementSummary elementen={o.elementen} />
          <p className="text-sm text-slate-500 flex items-center gap-1 mb-1"><MapPin className="w-3.5 h-3.5" /> {o.adres_postcode} {o.adres_gemeente}</p>
          <p className="text-sm text-slate-600 flex items-center gap-1 mb-3">
            <Building2 className="w-3.5 h-3.5" /> {Object.values(o.bedrijf_namen || {}).join(", ") || `${(o.bedrijf_ids || []).length} bedrijf/bedrijven`}
          </p>
          {(o.bijlagen?.length > 0) && (
            <p className="text-xs text-slate-400 mb-3">{o.bijlagen.length} bijlage(n) toegevoegd</p>
          )}
          <OfferteExpiry o={o} onExtend={onExtend} />
          {o.status === "concept" && (
            <div data-testid="offerte-pay-block" className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2">
              {isFreePeriod() ? (
                <>
                  <p className="text-xs font-semibold text-green-800">Deze aanvraag is nog niet verzonden. Versturen is <strong>gratis</strong> tot 31 december 2026.</p>
                  <Button data-testid="send-free-offerte-btn" onClick={() => onSendFree?.(o.id)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold">
                    <Gift className="w-4 h-4 mr-1" /> Gratis versturen
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold text-amber-800">Deze aanvraag is nog niet verzonden. Betaal €3 om de gekozen bedrijven te verwittigen.</p>
                  <Button data-testid="pay-offerte-btn" onClick={() => startCheckout({ kind: "offerte", offerteId: o.id })}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
                    <Euro className="w-4 h-4 mr-1" /> Betaal €3 en verstuur
                  </Button>
                </>
              )}
              <Button data-testid="delete-offerte-btn" onClick={() => onDelete(o.id)} variant="outline"
                className="w-full rounded-sm font-semibold border-slate-300 text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
              </Button>
            </div>
          )}
          {o.status === "open" && (
            <div>
              <p className="text-sm font-semibold text-green-700 mb-2">Verzonden — de bedrijven zijn verwittigd.</p>
              <div className="space-y-1.5">
                {(o.bedrijf_ids || []).map((bid) => {
                  const st = (o.bedrijf_status || {})[bid] || "open";
                  return (
                    <div key={bid} data-testid="offerte-bedrijf-status-row" className="flex items-center justify-between text-sm border border-slate-100 rounded-sm px-3 py-1.5">
                      <span className="text-slate-700 truncate">{(o.bedrijf_namen || {})[bid] || "Bedrijf"}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-sm shrink-0 ml-2 ${st === "offerte_verstuurd" ? "bg-green-100 text-green-700" : st === "niet_geinteresseerd" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-700"}`}>
                        {BEDRIJF_STATUS_LABEL[st]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
